import { useState } from 'react';
import DataTable from '../../components/DataTable';
import Pagination from '../../components/Pagination';
import SearchBar from '../../components/SearchBar';
import { useToast } from '../../context/ToastContext';
import ActivityIcon from './ActivityIcon';
import PlaceActivityForm from './PlaceActivityForm';
import {
  usePlaceActivityQueue,
  useAllPlaceActivities,
  useActivityCatalog,
  useCreatePlaceActivity,
  useSavePlaceActivities,
  useModeratePlaceActivities,
} from '../../hooks/usePlaceActivities';

const LIMIT = 20;

const TABS = [
  { key: 'queue', label: 'Queue' },
  { key: 'all', label: 'All' },
];

const ALL_FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'moderated', label: 'Moderated' },
  { key: 'unmoderated', label: 'Unmoderated' },
];

const PLACE_TYPES = ['place', 'city', 'state', 'country'];

// filter -> `moderated` query param ('all' omits it entirely)
const moderatedParam = (f) =>
  f === 'moderated' ? true : f === 'unmoderated' ? false : undefined;

function fmtDate(s) {
  if (!s) return '—';
  const d = new Date(s);
  return isNaN(d) ? s : d.toLocaleDateString();
}

const idsOf = (activities) => new Set((activities || []).map((a) => a.id));
// Stable per-row identity (place_name is unique per the API contract) so inline
// edits never bleed across rows even if `id` is missing.
const rowKey = (row) => row.id ?? row.place_name;

// Read-only chips (fallback when the catalog can't be loaded).
function ActivityChips({ activities }) {
  if (!activities || activities.length === 0) return <span className="muted">—</span>;
  return (
    <div className="act-chips">
      {activities.map((a) => (
        <span className="act-chip" key={a.id} title={a.name}>
          <ActivityIcon activity={a} />
          <span className="act-chip__name">{a.name}</span>
        </span>
      ))}
    </div>
  );
}

// Inline toggle picker rendered directly in the Activities cell.
function InlineActivityPicker({ catalog, selectedIds, onToggle, disabled }) {
  return (
    <div className="act-picker act-picker--inline">
      {catalog.map((a) => {
        const on = selectedIds.has(a.id);
        return (
          <button
            type="button"
            key={a.id}
            className={`act-option act-option--sm${on ? ' act-option--on' : ''}`}
            aria-pressed={on}
            disabled={disabled}
            onClick={() => onToggle(a.id)}
          >
            <ActivityIcon activity={a} />
            <span className="act-option__name">{a.name}</span>
          </button>
        );
      })}
    </div>
  );
}

export default function PlaceActivityModerationPage() {
  const toast = useToast();
  const [tab, setTab] = useState('queue');
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all'); // All-tab moderated filter
  const [typeFilter, setTypeFilter] = useState(''); // place_type filter ('' = all)
  const [hiddenIds, setHiddenIds] = useState(() => new Set()); // optimistic queue removal
  const [edits, setEdits] = useState(() => new Map()); // rowKey -> Set(activityId) of pending inline edits
  const [adding, setAdding] = useState(false);
  const [addError, setAddError] = useState(null);

  const isQueue = tab === 'queue';

  const queueQ = usePlaceActivityQueue(
    { page, limit: LIMIT, search, place_type: typeFilter || undefined },
    { enabled: isQueue }
  );
  const allQ = useAllPlaceActivities(
    { page, limit: LIMIT, search, moderated: moderatedParam(filter), place_type: typeFilter || undefined },
    { enabled: !isQueue }
  );
  const active = isQueue ? queueQ : allQ;
  const { data, isLoading, error } = active;

  const raw = data?.data || [];
  const rows = isQueue ? raw.filter((r) => !hiddenIds.has(r.id)) : raw;
  const total = data?.total;

  // Catalog powers the inline picker on every row, so load it up front.
  const catalogQ = useActivityCatalog({ enabled: true });
  const catalog = catalogQ.data?.data || [];
  const catalogReady = !catalogQ.isLoading && !catalogQ.error && catalog.length > 0;

  const hideRows = (ids) => setHiddenIds((prev) => new Set([...prev, ...ids]));
  const clearEdit = (key) =>
    setEdits((prev) => {
      if (!prev.has(key)) return prev;
      const next = new Map(prev);
      next.delete(key);
      return next;
    });

  // Current activity selection for a row: the pending edit, else its saved set.
  const selFor = (row) => edits.get(rowKey(row)) ?? idsOf(row.activities);

  const toggleActivity = (row, actId) =>
    setEdits((prev) => {
      const key = rowKey(row);
      const next = new Map(prev);
      const cur = new Set(next.get(key) ?? idsOf(row.activities));
      cur.has(actId) ? cur.delete(actId) : cur.add(actId);
      next.set(key, cur);
      return next;
    });

  // --- Reset helpers (page/edits) on any context change ---
  const resetView = () => {
    setPage(1);
    setHiddenIds(new Set());
    setEdits(new Map());
  };
  const changeTab = (key) => { if (key !== tab) { setTab(key); resetView(); } };
  const changeSearch = (v) => { setSearch(v); resetView(); };
  const changeFilter = (key) => { setFilter(key); resetView(); };
  const changeType = (v) => { setTypeFilter(v); resetView(); };
  const changePage = (p) => { setPage(p); setEdits(new Map()); };

  const createM = useCreatePlaceActivity({
    onSuccess: () => { toast.success('Mapping created'); setAdding(false); setAddError(null); },
    onError: (e) => {
      // 409 = name already exists (use update); 400 = bad input. Keep the form.
      setAddError(e.message || 'Create failed');
      toast.error(e.message || 'Create failed');
    },
  });

  const savingM = useSavePlaceActivities({
    onError: (e) => toast.error(e.message || 'Approve failed'),
  });

  const moderateM = useModeratePlaceActivities({
    onSuccess: (_data, vars) => {
      if (vars.moderated === false) toast.success('Sent back to queue');
    },
    onError: (e) => toast.error(e.message || 'Action failed'),
  });

  const busy = savingM.isPending || moderateM.isPending;

  // Build the batch payload item for one row (curl shape: place_name, optional
  // place_type/place_id, activities, moderated:true).
  const buildItem = (row) => ({
    place_name: row.place_name,
    ...(row.place_type ? { place_type: row.place_type } : {}),
    ...(row.place_id != null ? { place_id: row.place_id } : {}),
    activities: Array.from(selFor(row)),
    moderated: true,
  });

  // Approve rows as a single batch POST. Empty selections are rejected by the
  // server (400), so guard here and name the offending places.
  const approveRows = (list) => {
    if (!list.length) return;
    const empty = list.filter((r) => selFor(r).size === 0);
    if (empty.length) {
      toast.error(`Select at least one activity for: ${empty.map((r) => r.place_name).join(', ')}`);
      return;
    }
    const ids = list.map((r) => r.id);
    savingM
      .mutate(list.map(buildItem))
      .then(() => {
        hideRows(ids); // now moderated → leaves the queue
        list.forEach((r) => clearEdit(rowKey(r)));
        toast.success(list.length > 1 ? `Approved ${list.length}` : 'Approved');
      })
      .catch(() => {});
  };

  const sendToQueue = (row) => moderateM.mutate({ ids: [row.id], moderated: false });

  const openAdd = () => { setAddError(null); setAdding(true); };

  // --- Columns ---
  const columns = [
    { key: 'place_name', header: 'Place', render: (r) => r.place_name || '—' },
    {
      key: 'place_type',
      header: 'Type',
      render: (r) =>
        r.place_type ? <span className="badge">{r.place_type}</span> : <span className="muted">—</span>,
    },
    {
      key: 'activities',
      header: 'Activities',
      render: (r) =>
        catalogReady ? (
          <InlineActivityPicker
            catalog={catalog}
            selectedIds={selFor(r)}
            onToggle={(actId) => toggleActivity(r, actId)}
            disabled={busy}
          />
        ) : (
          <ActivityChips activities={r.activities} />
        ),
    },
    { key: 'created_at', header: 'Created', render: (r) => fmtDate(r.created_at) },
  ];
  if (!isQueue) {
    columns.push({
      key: 'moderated',
      header: 'Status',
      render: (r) => (
        <span className={`badge${r.moderated ? ' badge--app' : ' badge--itinerary'}`}>
          {r.moderated ? 'Moderated' : 'Unmoderated'}
        </span>
      ),
    });
  }

  // Per-row actions only on the All tab (approve one, or send moderated → queue).
  const rowActions = !isQueue
    ? (row) =>
        row.moderated ? (
          <button className="btn btn--sm btn--ghost" disabled={busy} onClick={() => sendToQueue(row)}>
            Send to queue
          </button>
        ) : (
          <button className="btn btn--sm btn--primary" disabled={busy} onClick={() => approveRows([row])}>
            Approve
          </button>
        )
    : undefined;

  // Queue approves the whole page at once, from the top and the bottom.
  const approveAllBtn = (
    <button
      className="btn btn--primary"
      disabled={busy || rows.length === 0}
      onClick={() => approveRows(rows)}
    >
      {busy ? 'Approving…' : `Approve all (${rows.length})`}
    </button>
  );

  return (
    <div className="page">
      <div className="page__header">
        <h1>Place Activities</h1>
        <div className="page__header-actions">
          <button className="btn" onClick={openAdd}>Add mapping</button>
          {isQueue && approveAllBtn}
        </div>
      </div>

      <div className="page__toolbar page__toolbar--row">
        <div className="seg">
          {TABS.map((t) => (
            <button
              key={t.key}
              type="button"
              className={`seg__btn${tab === t.key ? ' seg__btn--active' : ''}`}
              onClick={() => changeTab(t.key)}
            >
              {t.label}
            </button>
          ))}
        </div>
        <SearchBar value={search} onSearch={changeSearch} placeholder="Search by place name…" />
        <select
          className="toolbar-select"
          value={typeFilter}
          onChange={(e) => changeType(e.target.value)}
          aria-label="Filter by place type"
        >
          <option value="">All types</option>
          {PLACE_TYPES.map((t) => (
            <option key={t} value={t}>{t}</option>
          ))}
        </select>
        {!isQueue && (
          <div className="seg">
            {ALL_FILTERS.map((f) => (
              <button
                key={f.key}
                type="button"
                className={`seg__btn${filter === f.key ? ' seg__btn--active' : ''}`}
                onClick={() => changeFilter(f.key)}
              >
                {f.label}
              </button>
            ))}
          </div>
        )}
      </div>

      <DataTable
        columns={columns}
        rows={rows}
        isLoading={isLoading}
        error={error}
        emptyText={isQueue ? 'Moderation queue is empty.' : 'No mappings found.'}
        actions={rowActions}
      />

      {data && total != null && (
        <Pagination page={page} limit={LIMIT} total={total} onPage={changePage} />
      )}

      {isQueue && rows.length > 0 && (
        <div className="page__footer-actions">{approveAllBtn}</div>
      )}

      <PlaceActivityForm
        open={adding}
        mode="add"
        catalog={catalog}
        catalogLoading={catalogQ.isLoading}
        catalogError={catalogQ.error}
        pending={createM.isPending}
        serverError={addError}
        onClose={() => setAdding(false)}
        onSubmit={(body) => createM.mutate(body)}
      />
    </div>
  );
}
